import { DemoServices } from '@services/DemoServices';
import { Mocks } from '@services/Mocks';
import { Logger } from '@utils/Logger';
import Utils from '@utils/Utils';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, utcToZonedTime } from 'date-fns-tz';
import { Application } from '@core/Application';
import { Analytics } from '@core/Analytics/Analytics';

export interface Analisys {
    hsTrabajadasText: string
    subtext: string
    color: 'green' | 'red' | 'yellow' | 'gray'
    sign: string
    xlsExtraHours: string
    xlsTotalHours: string
}

export interface PresenteeismRecord {
    userID: string
    colaborador: string
    date: string
    checkinTime: string
    checkoutTime: string
    analysis: Analisys
    buildingName: string
}

export enum ViewState {
    initial,
    searching,
    loading,
    loaded,
    empty,
    error,
}

function transformData(data) {
    let result = [];
    for (let buildingID in data.buildings) {
        for (let userID in data.buildings[buildingID].user_records) {
            let user = data.buildings[buildingID].user_records[userID];
            for (let recordID in data.buildings[buildingID].user_records[userID].records) {
                let record = data.buildings[buildingID].user_records[userID].records[recordID];

                let convertedRecord = transformRecord(record)

                result.push({
                    numeroWhatsapp: userID,
                    colaborador: user.name,
                    photo: user.imageURL,
                    ...convertedRecord,
                    edificio: data.buildings[buildingID].name,
                });
            }
        }
    } 
    return result;
}

function minutesToHours(minutes) {
    // Round to the nearest whole number to avoid floating-point arithmetic issues
    const roundedMinutes = Math.round(minutes);

    // Calculate the number of hours and the remaining minutes
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;

    // Format the hours and minutes to ensure two digits
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = remainingMinutes.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}`;
}

function transformRecord(record) {

    let dateCheckin = new Date(record.checkInTimestamp)
    let orgTimezone = Application.shared().timezone

    let formattedDate = dateCheckin.toLocaleDateString('es-AR', {day: '2-digit', month: '2-digit', year: 'numeric', timeZone: orgTimezone})
    
    let zonedDate = utcToZonedTime(dateCheckin, orgTimezone)
    let ingreso = format(zonedDate, 'HH:mm', { timeZone: orgTimezone });
    let egreso = 'Pendiente'

    let analisisText = "No se registró salida aún".localized()
    let subtext = "Sin información aún".localized()
    let color = 'gray'
    let sign = " "

    let xlsTotalHours = "Sin información aún".localized()
    let xlsExtraHours = "Sin información aún".localized()
    let buildingName = record.edificio

    if (record.checkOutTimestamp !== 0) {
        //Tiene check-out
        let dateCheckout = new Date(record.checkOutTimestamp)
        let zonedCheckout = utcToZonedTime(dateCheckout, orgTimezone)
        egreso = format(zonedCheckout, 'HH:mm', { timeZone: orgTimezone });
        analisisText = `${minutesToHours(record.analisis.totalMinutesWorked)} horas trabajadas`

        xlsTotalHours = minutesToHours(record.analisis.totalMinutesWorked)

        if (record.hasShift) {
            //Tiene check-out y turno asignado

            let expectedMinutes = record.analisis.expectedMinutesWorked
            let totalMinutes = record.analisis.totalMinutesWorked
            let difference = Math.round(totalMinutes - expectedMinutes)

            let minutesBetween = Math.abs(difference)
            xlsExtraHours = `${minutesToHours(minutesBetween)}`
            if (difference > 0) {
                subtext = `${minutesToHours(minutesBetween)} horas extra`
                sign = '+'
            } else if (difference < 0) {
                subtext = `${minutesToHours(minutesBetween)} horas faltantes`
                sign = '-'
                xlsExtraHours = `${sign}${minutesToHours(minutesBetween)}`
            } else if (Math.abs(difference) === 0) {
                subtext = `Jornada completada totalmente`
                sign = '='
            }

            if (record.isLateCheckin == true) {
                color = 'red'
            } else if (record.isLateCheckin == false) {
                color = 'green'
            }
        } else {
            //Tiene check-out pero no tiene turno asignado
            subtext = "Sin turno asignado".localized()
            color = 'gray'
        }
    } else {
        // No tiene check-out
        analisisText = "No se registró salida aún".localized()

        if (record.hasShift) {
            // No tiene check-out, pero tiene turno asignado

            if (record.isLateCheckin === true) {
                color = 'red'
                subtext = 'Llegada tarde'.localized()
            } else if (record.isLateCheckin === false) {
                color = 'green'
                subtext = 'Llegada en tiempo'.localized()
            }
        } else {
            // No tiene check-out, ni turno asignado
            subtext = "Sin información aún".localized()
        }
    }


    return {
        date: formattedDate,
        checkinTime: ingreso,
        checkoutTime: egreso,
        analysis: {
            hsTrabajadasText: analisisText,
            subtext: subtext,
            color: color,
            sign: sign,
            xlsExtraHours: xlsExtraHours,
            xlsTotalHours: xlsTotalHours
        },
    }
}


export const useCheckinsViewModel = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [presenteeismRecords, setPresenteeismRecords] = useState<Array<PresenteeismRecord>>([])
    const [visiblePresenteeismRecords, setVisiblePresenteeismRecords] = useState<Array<PresenteeismRecord>>([])
    const [viewState, setViewState] = useState<ViewState>(ViewState.initial)
    const [searchText, setSearchText] = useState<string>("")
    const [placeholderText, setPlaceholderText] = useState<string>("")
    const [serviceError, setServiceError] = useState<string>("")
    const [qpID, setQPID] = useState<string>("")

    const analytics = new AnalyticsHelper()

    const viewDidAppear = async () => {
        let currentQPID = location.state.qpID
        setQPID(currentQPID)
        reloadData()
        analytics.trackScreenShown()
    }

    const reloadData = async () => {
        setViewState(ViewState.loading)
        setServiceError("")        
        try {
            let service = new DemoServices()
            let records = await service.getPresenteeismTable()
            let transformedRecords = transformData(records)
            let newViewState = transformedRecords.length == 0 ? ViewState.empty : ViewState.loaded
            Logger.logObject("transformed", transformedRecords)
            setPresenteeismRecords(transformedRecords)
            setVisiblePresenteeismRecords(transformedRecords)
            setSearchText("")
            definePlaceholderText()
            setViewState(newViewState)
        } catch (error) {
            setViewState(ViewState.error)
            setServiceError('Hubo un error al intentar traer la información'.localized())
        }
    }

    const definePlaceholderText = () => {
        setPlaceholderText(
            viewState == ViewState.searching ? 
                "No se han encontrado fichadas".localized() :
                "No hay fichadas todavía en el día de hoy.".localized()
        )
    }

    const search = async (text: string) => {
        if (viewState == ViewState.loading) {
            return
        }
        setSearchText(text)
        if (text.trim().length == 0) {
            setViewState(ViewState.loaded)
            setVisiblePresenteeismRecords(
                presenteeismRecords
            )
            return
        }
        setViewState(ViewState.searching)
        let records = presenteeismRecords.filter(
            (record) =>  record.colaborador.toLowerCase().includes(text.toLocaleLowerCase())
        )
        setVisiblePresenteeismRecords(
            records
        )
        definePlaceholderText()
    }

    return {
        // state
        visiblePresenteeismRecords,
        viewState,
        searchText,
        placeholderText,
        serviceError,
        qpID,

        // setters
        setSearchText,
        setPlaceholderText,

        // actions
        reloadData,
        search,
        viewDidAppear,
        analytics,
    }
} 

class AnalyticsHelper {
    private analytics: Analytics
    constructor(analytics: Analytics = Application.shared().coreDependencies.analytics) {
        this.analytics = analytics
    }

    async trackScreenShown() {
        this.analytics.track({
            eventType: 'AutoDemo.CheckinScreen.Shown',
        })
    }

    async trackQRCodeButtonTapped() {
        this.analytics.track({
            eventType: 'AutoDemo.CheckinScreen.QRCodeButtonTapped',
        })
    }

    async trackPromotionalModalButtonTapped() {
        this.analytics.track({
            eventType: 'AutoDemo.CheckinScreen.PromotionalModalButtonTapped',
        })
    }

    async trackWhatsAppButtonTapped() {
        this.analytics.track({
            eventType: 'AutoDemo.CheckinScreen.WhatsAppButtonTapped',
        })
    }

}